'use strict';

/**
 * T315 Module Lock — policy schema, factor registry, and migration shim.
 *
 * LD-1  System name = "Module Lock" (no user-facing "PIN").
 * LD-4  Per-module policy of independent factors; combine all|any; v:1 +
 *       migratePolicy read-shim; T290 keyWrapEnvelopes[] forward-compat seam.
 * LD-8  Extensible factor registry — new factor types drop in without a
 *       schema migration.
 *
 * Pure, no I/O. Reused by the server policy store, the edit-gate, and the
 * browser UI. Custodian-of-ciphertext (LD-7): a policy stores ONLY
 * lock metadata, never plaintext, key material, or the lock secret.
 */

const POLICY_VERSION = 1;
const SCOPES = Object.freeze(['access', 'edit', 'both']);
const COMBINES = Object.freeze(['all', 'any']);
const CONTROL_MODES = Object.freeze(['invisible', 'readonly', 'ask']);

const DEFAULT_IDLE_MS = 15 * 60 * 1000; // LD-11 — 15 min idle default

// ── Factor registry (LD-8) ──
// Built-in factors. registerFactorType() lets V2 factors (webauthn,
// time-window, second-person, location) register without schema migration.
const _factorRegistry = new Map();

function registerFactorType(type, spec) {
  if (typeof type !== 'string' || !type) throw new TypeError('factor type required');
  _factorRegistry.set(type, Object.assign({ defaultScope: 'access' }, spec || {}));
}

function listFactorTypes() {
  return Array.from(_factorRegistry.keys());
}

function isFactorType(type) {
  return _factorRegistry.has(type);
}

// Passphrase (A1) and Control (A2) ship in V1.
registerFactorType('passphrase', { defaultScope: 'access' });
registerFactorType('control', { defaultScope: 'access', defaultMode: 'invisible' });

function defaultFactor(type) {
  const spec = _factorRegistry.get(type) || { defaultScope: 'access' };
  const f = { enabled: false, scope: spec.defaultScope || 'access' };
  if (type === 'control') f.mode = spec.defaultMode || 'invisible';
  return f;
}

/**
 * Build a fresh, fully-defaulted policy for a module. No factor enabled.
 */
function createDefaultPolicy(moduleId) {
  return {
    v: POLICY_VERSION,
    moduleId: moduleId || null,
    factors: {
      passphrase: defaultFactor('passphrase'),
      control: defaultFactor('control'),
    },
    combine: 'all',
    autoRelock: { idleMs: DEFAULT_IDLE_MS, onTabClose: true },
    // T290 forward-compat seam (LD-4) — per-member section-DEK wraps register
    // here as an additional factor type; empty in v1.
    keyWrapEnvelopes: [],
  };
}

/**
 * Read-shim: take a raw (possibly older / partial) policy and return a
 * fully-defaulted v:1 policy. Idempotent.
 */
function migratePolicy(raw) {
  const base = createDefaultPolicy(raw && raw.moduleId);
  if (!raw || typeof raw !== 'object') return base;

  const out = base;
  out.moduleId = raw.moduleId != null ? raw.moduleId : base.moduleId;

  const rf = (raw.factors && typeof raw.factors === 'object') ? raw.factors : {};
  // Materialize every registered factor type, merging raw values.
  for (const type of listFactorTypes()) {
    const def = defaultFactor(type);
    const r = rf[type] && typeof rf[type] === 'object' ? rf[type] : {};
    const merged = Object.assign({}, def, r);
    if (merged.scope == null || SCOPES.indexOf(merged.scope) === -1) merged.scope = def.scope;
    if (typeof merged.enabled !== 'boolean') merged.enabled = false;
    out.factors[type] = merged;
  }
  // Preserve any unknown raw factor types verbatim (forward-compat) but they
  // are validated on write — keep so a future client can still read them.
  for (const k of Object.keys(rf)) {
    if (!out.factors[k]) out.factors[k] = Object.assign({}, rf[k]);
  }

  out.combine = COMBINES.indexOf(raw.combine) !== -1 ? raw.combine : 'all';

  const ar = raw.autoRelock && typeof raw.autoRelock === 'object' ? raw.autoRelock : {};
  out.autoRelock = {
    idleMs: typeof ar.idleMs === 'number' && ar.idleMs > 0 ? ar.idleMs : DEFAULT_IDLE_MS,
    onTabClose: ar.onTabClose === false ? false : true,
  };

  out.keyWrapEnvelopes = Array.isArray(raw.keyWrapEnvelopes) ? raw.keyWrapEnvelopes.slice() : [];
  out.v = POLICY_VERSION;
  return out;
}

/**
 * Validate a policy object. Throws on a malformed policy so the server
 * never persists something it could not enforce.
 */
function validatePolicy(p) {
  if (!p || typeof p !== 'object') throw new Error('Invalid policy: must be an object');
  if (p.v !== POLICY_VERSION) throw new Error('Invalid policy: unsupported version ' + p.v);
  if (!p.factors || typeof p.factors !== 'object') throw new Error('Invalid policy: factors missing');
  if (COMBINES.indexOf(p.combine) === -1) throw new Error('Invalid policy: combine must be all|any');
  if (!Array.isArray(p.keyWrapEnvelopes)) throw new Error('Invalid policy: keyWrapEnvelopes must be an array');
  for (const [type, f] of Object.entries(p.factors)) {
    if (!isFactorType(type)) throw new Error('Invalid policy: unknown factor type "' + type + '"');
    if (typeof f.enabled !== 'boolean') throw new Error('Invalid policy: factor "' + type + '" enabled must be boolean');
    if (SCOPES.indexOf(f.scope) === -1) throw new Error('Invalid policy: factor "' + type + '" scope must be access|edit|both');
    if (type === 'control' && f.enabled && CONTROL_MODES.indexOf(f.mode) === -1) {
      throw new Error('Invalid policy: control mode must be invisible|readonly|ask');
    }
  }
  return true;
}

/**
 * Return a NEW policy with one factor set/merged. Does not mutate input.
 */
function setFactor(p, type, factorPatch) {
  const next = JSON.parse(JSON.stringify(p));
  const existing = next.factors[type] || defaultFactor(type);
  next.factors[type] = Object.assign({}, existing, factorPatch || {});
  return next;
}

function hasAnyLock(p) {
  if (!p || !p.factors) return false;
  return Object.values(p.factors).some((f) => f && f.enabled === true);
}

/**
 * The set of factor types that GATE a given action (access | edit).
 * A factor gates `access` if its scope is access|both; gates `edit` if
 * its scope is edit|both.
 */
function requiredFactors(p, action) {
  if (!p || !p.factors) return [];
  const out = [];
  for (const [type, f] of Object.entries(p.factors)) {
    if (!f || !f.enabled) continue;
    const scope = f.scope || 'access';
    const gatesAccess = scope === 'access' || scope === 'both';
    const gatesEdit = scope === 'edit' || scope === 'both';
    if (action === 'access' && gatesAccess) out.push(type);
    else if (action === 'edit' && (gatesEdit || gatesAccess)) {
      // editing implies access — an access-gating factor also gates edit
      out.push(type);
    }
  }
  return out;
}

/**
 * Is the module locked for `action` given the set of factor types the user
 * has already satisfied this session? Honors combine all|any.
 */
function isLockedForAction(p, action, unlockedFactors) {
  const required = requiredFactors(p, action);
  if (required.length === 0) return false;
  const unlocked = unlockedFactors instanceof Set ? unlockedFactors : new Set(unlockedFactors || []);
  if ((p.combine || 'all') === 'any') {
    // any one required factor unlocks
    return !required.some((t) => unlocked.has(t));
  }
  // all required factors must be satisfied
  return !required.every((t) => unlocked.has(t));
}

// ── LD-1 — single string map (i18n seam). No user-facing "PIN". ──
const ML_STRINGS = Object.freeze({
  SYSTEM_NAME: 'Module Lock',
  ADD_LOCK: 'Add Module Lock',
  LOCK_NOW: 'Lock now',
  UNLOCK: 'Unlock module',
  PASSPHRASE: 'Passphrase',
  PASSPHRASE_HINT: 'Choose a passphrase (a number-only code works, but a longer phrase is stronger).',
  FORGOT: 'Forgot passphrase?',
  FORGOT_HELP: 'Your passphrase is recoverable with your account password (unlike your recovery seed).',
  WARN_EQUALS_PASSWORD: 'This matches your account password — using a different secret adds real protection.',
  WRONG_SECRET: 'Wrong passphrase',
  SETTINGS: 'Module Lock settings',
  CONTROL_FACTOR: 'Control access',
  RELOCKED: 'Module re-locked',
  UNLOCKED_STATE: 'Module unlocked',
});

module.exports = {
  POLICY_VERSION,
  SCOPES,
  COMBINES,
  CONTROL_MODES,
  DEFAULT_IDLE_MS,
  registerFactorType,
  listFactorTypes,
  isFactorType,
  createDefaultPolicy,
  migratePolicy,
  validatePolicy,
  setFactor,
  hasAnyLock,
  requiredFactors,
  isLockedForAction,
  ML_STRINGS,
};
